import type {
  ExpenseDataRecord,
  ExpensesNoteData,
  ExpensesNoteUpdateRequest,
  ExpenseUpdateRequest,
  IncomeDataRecord,
  IncomeNoteData,
  IncomeNoteUpdateRequest,
  IncomeUpdateRequest,
} from "@/shared/api/types";

export interface ProfileBudgetContext {
  customerEmploymentStatus?: string;
  partnerEmploymentStatus?: string;
  jointAccount?: string | boolean;
  accommodationType?: string;
  dependentChildrenUnder16?: number;
  dependentChildrenAged16Plus?: number;
  haveChildrenUnder18?: string; // "Y" | "N"
  customerAge?: number;
  partnerAge?: number;
}

export interface BudgetSnapshots {
  income: IncomeDataRecord | null;
  incomeNotes: IncomeNoteData | null;
  expenses: ExpenseDataRecord | null;
  expenseNotes: ExpensesNoteData | null;
}

type BudgetDomain = "income" | "expense";

type IncomeAmountKey =
  | "basicIncome"
  | "debtor2BasicIncome"
  | "otherSalaryWages1"
  | "jobseekerAllowanceContribute"
  | "incomeSupport"
  | "carerAllowance"
  | "childBenefit"
  | "housingBenefitLocalAllowance"
  | "maintenanceOrChildSupport"
  | "statePension"
  | "pensionCredit"
  | "privatePension"
  | "pensionOthers";

type IncomeNotesKey =
  | "basicIncomeNotes"
  | "debtor2BasicIncomeNotes"
  | "otherSalaryWages1Notes"
  | "jobseekerAllowanceContributeNotes"
  | "incomeSupportNotes"
  | "carerAllowanceNotes"
  | "childBenefitNotes"
  | "housingBenefitLocalAllowanceNotes"
  | "maintenanceOrChildSupportNotes"
  | "statePensionNotes"
  | "pensionCreditNotes"
  | "privatePensionNotes"
  | "pensionOthersNotes";

type ExpenseAmountKey =
  | "schoolUniform"
  | "clubsAndSchoolTrips"
  | "otherSchoolCosts"
  | "pocketMoneyAndSchoolTrips";

type ExpenseNotesKey =
  | "schoolUniformNotes"
  | "clubsAndSchoolTripsNotes"
  | "otherSchoolCostsNotes"
  | "pocketMoneyAndSchoolTripsNotes";

interface BaseFieldDefinition {
  id: string;
  domain: BudgetDomain;
  label: string;
  isVisible: (ctx: ProfileBudgetContext, snaps: BudgetSnapshots) => boolean;
}

interface IncomeFieldDefinition extends BaseFieldDefinition {
  domain: "income";
  amountKey: IncomeAmountKey;
  notesKey: IncomeNotesKey;
}

interface ExpenseFieldDefinition extends BaseFieldDefinition {
  domain: "expense";
  amountKey: ExpenseAmountKey;
  notesKey: ExpenseNotesKey;
}

type FieldDefinition = IncomeFieldDefinition | ExpenseFieldDefinition;

export interface ConfirmBeforeClearField {
  id: string;
  domain: BudgetDomain;
  label: string;
  beforeVisible: boolean;
  afterVisible: boolean;
  hasData: boolean;
}

export interface ConfirmBeforeClearPlan {
  needsConfirm: boolean;
  clearedLabels: string[];
  fields: ConfirmBeforeClearField[];
  clearPayloads: {
    incomeData?: IncomeUpdateRequest;
    incomeNotes?: IncomeNoteUpdateRequest;
    expenseData?: ExpenseUpdateRequest;
    expenseNotes?: ExpensesNoteUpdateRequest;
  };
}

const NON_WORKING_STATUSES: ReadonlySet<string> = new Set([
  "Unemployed",
  "Retired",
  "Carer",
  "Student",
  "Other",
  "Not working due to illness/disability",
]);

const WORKING_STATUSES: ReadonlySet<string> = new Set([
  "Full Time",
  "Part Time",
  "Self Employed",
]);

function normalizeEmploymentStatus(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  // Tolerate harmless formatting differences coming from schemas / backend.
  // Example: "Not working due to illness/disability" == "Not working due to illness/disability"
  return value.trim().replace(/\s+/g, " ").replace(/\s*\/\s*/g, "/");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Loose key helpers
 *
 * Some environments return inconsistent key formats from the backend, e.g.
 * - camelCase vs snake_case: `basicIncome` vs `basic_income`
 * - casing differences: `jobseekerAllowanceIncome` vs `jobSeekerAllowanceIncome`
 *
 * The rest of the app already maps values in a tolerant way (see budget mapping helpers),
 * so confirm-before-clear must do the same for both detection and clearing.
 */
function normalizeLooseKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function findKeyLoose(obj: Record<string, unknown>, desiredKey: string): string | undefined {
  const target = normalizeLooseKey(desiredKey);
  return Object.keys(obj).find((k) => normalizeLooseKey(k) === target);
}

function getLooseValue(obj: unknown, desiredKey: string): unknown {
  if (!isRecord(obj)) return undefined;

  const direct = obj[desiredKey];
  if (direct !== undefined) return direct;

  const match = findKeyLoose(obj, desiredKey);
  if (match && match !== desiredKey) {
    return obj[match];
  }

  return undefined;
}

function setLooseValue(
  obj: Record<string, unknown>,
  desiredKey: string,
  value: unknown
): void {
  // Always write canonical key
  obj[desiredKey] = value;

  // Also overwrite any existing variants (snake_case / casing) found on the payload.
  const target = normalizeLooseKey(desiredKey);
  Object.keys(obj).forEach((k) => {
    if (normalizeLooseKey(k) === target) {
      obj[k] = value;
    }
  });
}

function flagIsTrue(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "string") {
    const v = value.trim().toUpperCase();
    return v === "Y" || v === "YES" || v === "TRUE";
  }
  return false;
}

function jointAccountIsYes(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "string") return value.trim().toUpperCase() === "Y";
  return false;
}

function parseLooseNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Tolerate formatted currency/number strings from older APIs/UI layers:
  // "1,234.56", "£1,234.56", "  1234  "
  const normalized = trimmed.replace(/[,£\s]/g, "");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function hasPositiveAmount(value: unknown): boolean {
  const n = parseLooseNumber(value);
  return typeof n === "number" && n > 0;
}

function hasNonEmptyNotes(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function getNumber(obj: unknown, key: string): number | undefined {
  const raw = getLooseValue(obj, key);
  const n = parseLooseNumber(raw);
  return typeof n === "number" ? n : undefined;
}

function getString(obj: unknown, key: string): string | undefined {
  const raw = getLooseValue(obj, key);
  return typeof raw === "string" ? raw : undefined;
}

function getDependentCount(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function accommodationEligibleForHousingBenefit(accommodationType: unknown): boolean {
  if (typeof accommodationType !== "string") return false;
  const v = accommodationType.trim();
  // Schema includes both "Other" and "Others".
  if (v === "Other" || v === "Others") return true;
  if (v === "Tenant - Social" || v === "Tenant - Private") return true;
  // Future-safe: if tenant-like label changes but still starts with Tenant
  if (v.startsWith("Tenant")) return true;
  return false;
}

// Helper: returns true when at least one of the given keys has a positive amount
// in the record.  Used as a fallback when the explicit Y/N toggle flag was never
// persisted (common for newly-created accounts).
function anyPositiveAmount(record: unknown, keys: readonly string[]): boolean {
  if (!isRecord(record)) return false;
  return keys.some((k) => {
    const v = getLooseValue(record, k);
    return hasPositiveAmount(v);
  });
}

function haveBenefits(snaps: BudgetSnapshots): boolean {
  const income = snaps.income;
  if (flagIsTrue(getLooseValue(income, "haveBenefits"))) return true;
  // Fallback: check if any benefit amounts exist (flag may be missing on new accounts)
  return anyPositiveAmount(income, [
    "childBenefit", "housingBenefitLocalAllowance", "carerAllowance",
    "jobseekerAllowanceContribute", "incomeSupport",
    "maintenanceOrChildSupport",
  ]);
}

function havePension(snaps: BudgetSnapshots): boolean {
  const income = snaps.income;
  if (flagIsTrue(getLooseValue(income, "havePension"))) return true;
  return anyPositiveAmount(income, [
    "statePension", "pensionCredit", "privatePension", "pensionOthers",
  ]);
}

function haveOtherSources(snaps: BudgetSnapshots): boolean {
  const income = snaps.income;
  if (flagIsTrue(getLooseValue(income, "haveOtherIncome"))) return true;
  return anyPositiveAmount(income, ["maintenanceOrChildSupport"]);
}

function haveSchoolExpenses(snaps: BudgetSnapshots): boolean {
  const exp = snaps.expenses;
  if (flagIsTrue(getLooseValue(exp, "haveSchoolExpenses"))) return true;
  return anyPositiveAmount(exp, [
    "schoolUniform", "clubsAndSchoolTrips", "otherSchoolCosts",
  ]);
}

function haveLeisureExpenses(snaps: BudgetSnapshots): boolean {
  const exp = snaps.expenses;
  if (flagIsTrue(getLooseValue(exp, "haveCommsAndLeisureExpenses"))) return true;
  return anyPositiveAmount(exp, ["pocketMoneyAndSchoolTrips"]);
}

function hasPartner(ctx: ProfileBudgetContext): boolean {
  return jointAccountIsYes(ctx.jointAccount);
}

function dependentChildrenAny(ctx: ProfileBudgetContext): boolean {
  const under16 = getDependentCount(ctx.dependentChildrenUnder16);
  const aged16 = getDependentCount(ctx.dependentChildrenAged16Plus);
  return under16 + aged16 > 0;
}

function dependentChildrenCountsKnown(ctx: ProfileBudgetContext): boolean {
  const u = ctx.dependentChildrenUnder16;
  const a = ctx.dependentChildrenAged16Plus;
  return (
    typeof u === "number" ||
    typeof u === "string" ||
    typeof a === "number" ||
    typeof a === "string"
  );
}

function isEmploymentStatusUnemployed(status: unknown): boolean {
  return normalizeEmploymentStatus(status) === "Unemployed";
}

function isEmploymentStatusNonWorking(status: unknown): boolean {
  const normalized = normalizeEmploymentStatus(status);
  return typeof normalized === "string" && NON_WORKING_STATUSES.has(normalized);
}

function isEmploymentStatusRetired(status: unknown): boolean {
  return normalizeEmploymentStatus(status) === "Retired";
}

function isWorkingStatus(status: unknown): boolean {
  const normalized = normalizeEmploymentStatus(status);
  return typeof normalized === "string" && WORKING_STATUSES.has(normalized);
}

function salaryCustomerVisible(ctx: ProfileBudgetContext): boolean {
  // Align with Salary schema (`engine/schema/budget/income/1-salary.json`):
  // - Take home salary hides ONLY when employment status is "Unemployed".
  // Other statuses (e.g., Student/Retired/Carer/Other/Illness) are used for prompts/warnings,
  // but do not hide the basic income question.
  return !isEmploymentStatusUnemployed(ctx.customerEmploymentStatus);
}

function salaryPartnerVisible(ctx: ProfileBudgetContext): boolean {
  if (!hasPartner(ctx)) return false;
  // Align with Salary schema: partner salary hides when partnerEmploymentStatus === "Unemployed"
  // (and also when there's no partner / jointAccount is N).
  return !isEmploymentStatusUnemployed(ctx.partnerEmploymentStatus);
}

function salaryOtherVisible(ctx: ProfileBudgetContext): boolean {
  const customerNonWorking = isEmploymentStatusNonWorking(ctx.customerEmploymentStatus);
  const partnerNonWorking = isEmploymentStatusNonWorking(ctx.partnerEmploymentStatus);
  const noPartner = !hasPartner(ctx);
  // Other salary hides when customer is non-working AND (no partner OR partner is non-working)
  return !(customerNonWorking && (noPartner || partnerNonWorking));
}

function benefitUnemploymentLinkedVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveBenefits(snaps)) return false;
  return (
    isEmploymentStatusUnemployed(ctx.customerEmploymentStatus) ||
    (hasPartner(ctx) && isEmploymentStatusUnemployed(ctx.partnerEmploymentStatus))
  );
}

function benefitCarerAllowanceVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveBenefits(snaps)) return false;
  if (isWorkingStatus(ctx.customerEmploymentStatus)) return true;
  if (normalizeEmploymentStatus(ctx.customerEmploymentStatus) === "Carer") return true;
  if (hasPartner(ctx) && isWorkingStatus(ctx.partnerEmploymentStatus)) return true;
  if (
    hasPartner(ctx) &&
    normalizeEmploymentStatus(ctx.partnerEmploymentStatus) === "Carer"
  )
    return true;
  return false;
}

function benefitChildVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveBenefits(snaps)) return false;
  return dependentChildrenAny(ctx);
}

function benefitHousingVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveBenefits(snaps)) return false;
  return accommodationEligibleForHousingBenefit(ctx.accommodationType);
}

function otherSourcesMaintenanceVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveOtherSources(snaps)) return false;
  // Align with Child Benefit: maintenance/support should be driven by dependent counts.
  // Fall back to the legacy Y/N flag only when counts aren't available (older records).
  if (dependentChildrenCountsKnown(ctx)) return dependentChildrenAny(ctx);
  return typeof ctx.haveChildrenUnder18 === "string" && ctx.haveChildrenUnder18 === "Y";
}

function pensionStateOrCreditVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!havePension(snaps)) return false;
  const cAge = ctx.customerAge;
  const pAge = ctx.partnerAge;
  const cOk = typeof cAge === "number" && cAge >= 65;
  const pOk = hasPartner(ctx) && typeof pAge === "number" && pAge >= 65;
  return cOk || pOk;
}

function pensionPrivateOrOthersVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!havePension(snaps)) return false;
  const cAge = ctx.customerAge;
  const pAge = ctx.partnerAge;
  const c40 = typeof cAge === "number" && cAge >= 40;
  const p40 = hasPartner(ctx) && typeof pAge === "number" && pAge >= 40;
  if (c40 || p40) return true;

  const cRetired = isEmploymentStatusRetired(ctx.customerEmploymentStatus);
  const pRetired = hasPartner(ctx) && isEmploymentStatusRetired(ctx.partnerEmploymentStatus);

  const cLt40 = typeof cAge === "number" && cAge < 40;
  const pLt40 =
    !hasPartner(ctx) || (typeof pAge === "number" && pAge < 40) || typeof pAge !== "number";
  return cLt40 && pLt40 && (cRetired || pRetired);
}

function expenseSchoolVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveSchoolExpenses(snaps)) return false;
  return dependentChildrenAny(ctx);
}

function expensePocketMoneyVisible(ctx: ProfileBudgetContext, snaps: BudgetSnapshots): boolean {
  if (!haveLeisureExpenses(snaps)) return false;
  return dependentChildrenAny(ctx);
}

const FIELDS: readonly FieldDefinition[] = [
  // Salary
  {
    id: "salary.takeHomeSalary",
    domain: "income",
    label: "Take home salary",
    amountKey: "basicIncome",
    notesKey: "basicIncomeNotes",
    isVisible: (ctx) => salaryCustomerVisible(ctx),
  },
  {
    id: "salary.partnerTakeHomeSalary",
    domain: "income",
    label: "Partner take home salary",
    amountKey: "debtor2BasicIncome",
    notesKey: "debtor2BasicIncomeNotes",
    isVisible: (ctx) => salaryPartnerVisible(ctx),
  },
  {
    id: "salary.otherSalarySource",
    domain: "income",
    label: "Other salary source",
    amountKey: "otherSalaryWages1",
    notesKey: "otherSalaryWages1Notes",
    isVisible: (ctx) => salaryOtherVisible(ctx),
  },

  // Benefits (subset in scope)
  {
    id: "benefits.jobSeekerAllowanceContribute",
    domain: "income",
    label: "Job Seeker's Allowance (Contribution)",
    amountKey: "jobseekerAllowanceContribute",
    notesKey: "jobseekerAllowanceContributeNotes",
    isVisible: (ctx, snaps) => benefitUnemploymentLinkedVisible(ctx, snaps),
  },
  {
    id: "benefits.carerAllowance",
    domain: "income",
    label: "Carer's Allowance",
    amountKey: "carerAllowance",
    notesKey: "carerAllowanceNotes",
    isVisible: (ctx, snaps) => benefitCarerAllowanceVisible(ctx, snaps),
  },
  {
    id: "benefits.childBenefit",
    domain: "income",
    label: "Child Benefit",
    amountKey: "childBenefit",
    notesKey: "childBenefitNotes",
    isVisible: (ctx, snaps) => benefitChildVisible(ctx, snaps),
  },
  {
    id: "benefits.housingBenefitLocalAllowance",
    domain: "income",
    label: "Housing Benefit",
    amountKey: "housingBenefitLocalAllowance",
    notesKey: "housingBenefitLocalAllowanceNotes",
    isVisible: (ctx, snaps) => benefitHousingVisible(ctx, snaps),
  },

  // Other sources (maintenance)
  {
    id: "otherSources.childMaintenanceOrSupport",
    domain: "income",
    label: "Child Maintenance or Support",
    amountKey: "maintenanceOrChildSupport",
    notesKey: "maintenanceOrChildSupportNotes",
    isVisible: (ctx, snaps) => otherSourcesMaintenanceVisible(ctx, snaps),
  },

  // Pension
  {
    id: "pension.statePension",
    domain: "income",
    label: "State Pension",
    amountKey: "statePension",
    notesKey: "statePensionNotes",
    isVisible: (ctx, snaps) => pensionStateOrCreditVisible(ctx, snaps),
  },
  {
    id: "pension.pensionCredit",
    domain: "income",
    label: "Pension Credit",
    amountKey: "pensionCredit",
    notesKey: "pensionCreditNotes",
    isVisible: (ctx, snaps) => pensionStateOrCreditVisible(ctx, snaps),
  },
  {
    id: "pension.privatePension",
    domain: "income",
    label: "Private Pension",
    amountKey: "privatePension",
    notesKey: "privatePensionNotes",
    isVisible: (ctx, snaps) => pensionPrivateOrOthersVisible(ctx, snaps),
  },
  {
    id: "pension.pensionOthers",
    domain: "income",
    label: "Other Pension",
    amountKey: "pensionOthers",
    notesKey: "pensionOthersNotes",
    isVisible: (ctx, snaps) => pensionPrivateOrOthersVisible(ctx, snaps),
  },

  // Expenses (school + pocket money)
  {
    id: "expenses.schoolUniform",
    domain: "expense",
    label: "School uniforms",
    amountKey: "schoolUniform",
    notesKey: "schoolUniformNotes",
    isVisible: (ctx, snaps) => expenseSchoolVisible(ctx, snaps),
  },
  {
    id: "expenses.clubsAndSchoolTrips",
    domain: "expense",
    label: "School clubs and trips",
    amountKey: "clubsAndSchoolTrips",
    notesKey: "clubsAndSchoolTripsNotes",
    isVisible: (ctx, snaps) => expenseSchoolVisible(ctx, snaps),
  },
  {
    id: "expenses.otherSchoolCosts",
    domain: "expense",
    label: "Other school expenses",
    amountKey: "otherSchoolCosts",
    notesKey: "otherSchoolCostsNotes",
    isVisible: (ctx, snaps) => expenseSchoolVisible(ctx, snaps),
  },
  {
    id: "expenses.pocketMoney",
    domain: "expense",
    label: "Pocket money and school trips",
    amountKey: "pocketMoneyAndSchoolTrips",
    notesKey: "pocketMoneyAndSchoolTripsNotes",
    isVisible: (ctx, snaps) => expensePocketMoneyVisible(ctx, snaps),
  },
] satisfies readonly FieldDefinition[];

function getAmountValue(
  field: FieldDefinition,
  snaps: BudgetSnapshots
): number | undefined {
  const source = field.domain === "income" ? snaps.income : snaps.expenses;
  return getNumber(source, field.amountKey);
}

function getNotesValue(field: FieldDefinition, snaps: BudgetSnapshots): string | undefined {
  const source =
    field.domain === "income" ? snaps.incomeNotes : snaps.expenseNotes;
  return getString(source, field.notesKey);
}

function applyIncomeAmountClear(payload: IncomeUpdateRequest, key: IncomeAmountKey): void {
  setLooseValue(payload as unknown as Record<string, unknown>, key, 0);
}

function applyIncomeNotesClear(payload: IncomeNoteUpdateRequest, key: IncomeNotesKey): void {
  setLooseValue(payload as unknown as Record<string, unknown>, key, "");
}

function applyExpenseAmountClear(payload: ExpenseUpdateRequest, key: ExpenseAmountKey): void {
  setLooseValue(payload as unknown as Record<string, unknown>, key, 0);
}

function applyExpenseNotesClear(payload: ExpensesNoteUpdateRequest, key: ExpenseNotesKey): void {
  setLooseValue(payload as unknown as Record<string, unknown>, key, "");
}

function buildIncomeDataClearPayload(params: {
  income: IncomeDataRecord;
  userId: number;
  nowIso: string;
  amountKeysToClear: readonly IncomeAmountKey[];
}): IncomeUpdateRequest {
  // IMPORTANT: backend treats this as a PUT-style upsert, so we must send the
  // full record (GET snapshot) plus our intended changes, otherwise fields
  // not present in the payload can be dropped/zeroed server-side.
  const payload: IncomeUpdateRequest = {
    ...(params.income as unknown as Record<string, unknown>),
    id: params.income.id,
    customerId: params.income.customerId,
    lastUpdatedBy: params.userId,
    lastUpdateDate: params.nowIso,
  } as IncomeUpdateRequest;

  for (const key of params.amountKeysToClear) {
    applyIncomeAmountClear(payload, key);
  }
  return payload;
}

function buildIncomeNotesClearPayload(params: {
  incomeNotes: IncomeNoteData;
  customerId: number;
  userId: number;
  nowIso: string;
  notesKeysToClear: readonly IncomeNotesKey[];
}): IncomeNoteUpdateRequest {
  // Same rationale as income PUT: send the full notes record plus overrides.
  const payload: IncomeNoteUpdateRequest = {
    ...(params.incomeNotes as unknown as Record<string, unknown>),
    id: params.incomeNotes.id,
    customerId: params.customerId,
    lastUpdatedBy: params.userId,
    lastUpdateDate: params.nowIso,
  } as IncomeNoteUpdateRequest;

  for (const key of params.notesKeysToClear) {
    applyIncomeNotesClear(payload, key);
  }

  return payload;
}

function buildExpenseDataClearPayload(params: {
  expense: ExpenseDataRecord;
  customerId: number;
  userId: number;
  nowIso: string;
  amountKeysToClear: readonly ExpenseAmountKey[];
}): ExpenseUpdateRequest {
  // Same rationale as income/notes PUT: send the full expense record.
  const payload: ExpenseUpdateRequest = {
    ...(params.expense as unknown as Record<string, unknown>),
    id: params.expense.id,
    customerId: params.customerId,
    lastUpdatedBy: params.userId,
    lastUpdateDate: params.nowIso,
  } as ExpenseUpdateRequest;

  for (const key of params.amountKeysToClear) {
    applyExpenseAmountClear(payload, key);
  }
  return payload;
}

function buildExpenseNotesClearPayload(params: {
  expenseNotes: ExpensesNoteData;
  customerId: number;
  userId: number;
  nowIso: string;
  notesKeysToClear: readonly ExpenseNotesKey[];
}): ExpensesNoteUpdateRequest {
  const payload: ExpensesNoteUpdateRequest = {
    ...(params.expenseNotes as unknown as Record<string, unknown>),
    id: params.expenseNotes.id,
    customerId: params.customerId,
    lastUpdatedBy: params.userId,
    lastUpdateDate: params.nowIso,
  } as ExpensesNoteUpdateRequest;

  for (const key of params.notesKeysToClear) {
    applyExpenseNotesClear(payload, key);
  }
  return payload;
}

export function computeConfirmBeforeClearPlan(params: {
  before: ProfileBudgetContext;
  after: ProfileBudgetContext;
  snaps: BudgetSnapshots;
  userId: number;
  nowIso?: string;
}): ConfirmBeforeClearPlan {
  const nowIso = typeof params.nowIso === "string" ? params.nowIso : new Date().toISOString();

  const fields: ConfirmBeforeClearField[] = FIELDS.map((def) => {
    const beforeVisible = def.isVisible(params.before, params.snaps);
    const afterVisible = def.isVisible(params.after, params.snaps);
    const amount = getAmountValue(def, params.snaps);
    const notes = getNotesValue(def, params.snaps);
    const hasData = hasPositiveAmount(amount) || hasNonEmptyNotes(notes);
    return {
      id: def.id,
      domain: def.domain,
      label: def.label,
      beforeVisible,
      afterVisible,
      hasData,
    };
  });

  const toClear = fields.filter((f) => f.beforeVisible && !f.afterVisible && f.hasData);
  const clearedLabels = toClear.map((f) => f.label);

  const incomeAmountKeys = toClear
    .filter((f) => f.domain === "income")
    .map((f) => {
      const def = FIELDS.find((d) => d.id === f.id);
      return def ? def.amountKey : null;
    })
    .filter((k): k is IncomeAmountKey => typeof k === "string");

  const incomeNotesKeys = toClear
    .filter((f) => f.domain === "income")
    .map((f) => {
      const def = FIELDS.find((d) => d.id === f.id);
      return def ? def.notesKey : null;
    })
    .filter((k): k is IncomeNotesKey => typeof k === "string");

  const expenseAmountKeys = toClear
    .filter((f) => f.domain === "expense")
    .map((f) => {
      const def = FIELDS.find((d) => d.id === f.id);
      return def ? def.amountKey : null;
    })
    .filter((k): k is ExpenseAmountKey => typeof k === "string");

  const expenseNotesKeys = toClear
    .filter((f) => f.domain === "expense")
    .map((f) => {
      const def = FIELDS.find((d) => d.id === f.id);
      return def ? def.notesKey : null;
    })
    .filter((k): k is ExpenseNotesKey => typeof k === "string");

  const clearPayloads: ConfirmBeforeClearPlan["clearPayloads"] = {};

  if (incomeAmountKeys.length > 0 && params.snaps.income) {
    clearPayloads.incomeData = buildIncomeDataClearPayload({
      income: params.snaps.income,
      userId: params.userId,
      nowIso,
      amountKeysToClear: incomeAmountKeys,
    });
  }

  if (incomeNotesKeys.length > 0 && params.snaps.incomeNotes && params.snaps.income) {
    clearPayloads.incomeNotes = buildIncomeNotesClearPayload({
      incomeNotes: params.snaps.incomeNotes,
      customerId: params.snaps.income.customerId,
      userId: params.userId,
      nowIso,
      notesKeysToClear: incomeNotesKeys,
    });
  }

  if (expenseAmountKeys.length > 0 && params.snaps.expenses) {
    const customerIdRaw = getNumber(params.snaps.expenses, "customerId");
    const customerId =
      typeof customerIdRaw === "number"
        ? customerIdRaw
        : typeof params.snaps.expenses.customerNumber === "string"
          ? parseInt(params.snaps.expenses.customerNumber, 10)
          : NaN;
    if (Number.isFinite(customerId)) {
      clearPayloads.expenseData = buildExpenseDataClearPayload({
        expense: params.snaps.expenses,
        customerId,
        userId: params.userId,
        nowIso,
        amountKeysToClear: expenseAmountKeys,
      });
    }
  }

  if (expenseNotesKeys.length > 0 && params.snaps.expenseNotes && params.snaps.expenses) {
    const customerIdRaw = getNumber(params.snaps.expenses, "customerId");
    const customerId =
      typeof customerIdRaw === "number"
        ? customerIdRaw
        : typeof params.snaps.expenses.customerNumber === "string"
          ? parseInt(params.snaps.expenses.customerNumber, 10)
          : NaN;
    if (Number.isFinite(customerId)) {
      clearPayloads.expenseNotes = buildExpenseNotesClearPayload({
        expenseNotes: params.snaps.expenseNotes,
        customerId,
        userId: params.userId,
        nowIso,
        notesKeysToClear: expenseNotesKeys,
      });
    }
  }

  return {
    needsConfirm: toClear.length > 0,
    clearedLabels,
    fields,
    clearPayloads,
  };
}

